import { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../../config";
import SongCard from "./SongCard";
import SongReviewModal from "./SongReviewModal";
import LoadingScreen from "../../common/LoadingScreen";
import { getStorageFootprint } from "../../../utils/songStorage";
import { authFetch, getUser } from "../../../utils/auth";
import "../../../assets/css/music.css";

function SearchIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

function PlusIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
    );
}

function StorageIcon({ size = 15 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
    );
}

function MusicDash({ lang_id }) {
    // Active tab: 'course' or 'my_library'
    const [activeTab, setActiveTab] = useState("course");

    // Course songs & user custom songs
    const [courseSongs, setCourseSongs] = useState([]);
    const [customSongs, setCustomSongs] = useState([]);
    const [loadingCourse, setLoadingCourse] = useState(true);
    const [loadingCustom, setLoadingCustom] = useState(true);
    const [error, setError] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Review Modal state
    const [selectedVideo, setSelectedVideo] = useState(null);

    // Storage footprint state
    const [storageStats, setStorageStats] = useState({ count: 0, totalMB: "0.0" });
    const [toastMessage, setToastMessage] = useState(null);

    const currentUser = getUser();
    const user_id = (currentUser && currentUser.id) || new URLSearchParams(window.location.search).get("user_id") || "0";

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3500);
    };

    const updateStorageStats = useCallback(() => {
        getStorageFootprint()
            .then((stats) => {
                setStorageStats(stats);
            })
            .catch(console.error);
    }, []);

    // Fetch Course Songs
    useEffect(() => {
        if (lang_id === undefined || lang_id === null) return;
        setLoadingCourse(true);
        fetch(`${API_BASE_URL}/api/songs?lang_id=${lang_id}`)
            .then((res) => res.json())
            .then((data) => {
                if (data.error) {
                    setError(true);
                } else {
                    setCourseSongs(data.songs || []);
                }
            })
            .catch((err) => {
                console.error(err);
                setError(true);
            })
            .finally(() => {
                setLoadingCourse(false);
            });
    }, [lang_id]);

    // Fetch User Custom Songs
    const fetchCustomSongs = useCallback(() => {
        if (lang_id === undefined || lang_id === null) return;
        setLoadingCustom(true);
        authFetch(`${API_BASE_URL}/api/songs/custom?lang_id=${lang_id}`)
            .then((res) => res.json())
            .then((data) => {
                setCustomSongs(data.songs || []);
            })
            .catch((err) => {
                console.error("Error fetching custom songs:", err);
            })
            .finally(() => {
                setLoadingCustom(false);
            });
    }, [lang_id]);

    useEffect(() => {
        fetchCustomSongs();
        updateStorageStats();
    }, [fetchCustomSongs, updateStorageStats]);

    // Search YouTube
    const handleSearch = (e) => {
        if (e) e.preventDefault();
        const q = searchQuery.trim();
        if (!q) return;

        setIsSearching(true);
        setHasSearched(true);
        fetch(`${API_BASE_URL}/api/songs/search?q=${encodeURIComponent(q)}&lang_id=${lang_id}`)
            .then((res) => res.json())
            .then((data) => {
                setSearchResults(data.results || []);
            })
            .catch((err) => {
                console.error("Search error:", err);
                setSearchResults([]);
            })
            .finally(() => {
                setIsSearching(false);
            });
    };

    // Callback when modal completes saving a song
    const handleSongSaved = (newSong) => {
        setSelectedVideo(null);
        showToast(`Saved "${newSong.title}" to local device library!`);
        fetchCustomSongs();
        updateStorageStats();
        setActiveTab("my_library");
    };

    const handleSongDeleted = (deletedSongId) => {
        setCustomSongs((prev) => prev.filter((s) => s.id !== deletedSongId));
        updateStorageStats();
        showToast("Song removed from device and library.");
    };

    return (
        <div className="music-dashboard-root">
            {/* Toast notification */}
            {toastMessage && (
                <div className="music-dash-toast">
                    {toastMessage}
                </div>
            )}

            {/* Navigation Tabs + Storage Badge */}
            <div className="music-dash-top-bar">
                <div className="music-dash-nav-tabs">
                    <button
                        className={`music-nav-tab-btn ${activeTab === "course" ? "active" : ""}`}
                        onClick={() => setActiveTab("course")}
                    >
                        Course Songs ({courseSongs.length})
                    </button>
                    <button
                        className={`music-nav-tab-btn ${activeTab === "my_library" ? "active" : ""}`}
                        onClick={() => setActiveTab("my_library")}
                    >
                        My Library ({customSongs.length})
                    </button>
                </div>

                <div className="music-storage-badge" title="Audio and lyrics are kept locally on your device">
                    <StorageIcon size={14} />
                    <span>{storageStats.count} stored · {storageStats.totalMB} MB</span>
                </div>
            </div>

            {/* TAB 1: COURSE SONGS */}
            {activeTab === "course" && (
                <div className="music-tab-content">
                    {loadingCourse && <LoadingScreen inline title="Loading course songs..." size={40} />}
                    {error && <p className="music-dash-status-msg">Error loading songs. Please try again later.</p>}
                    {!loadingCourse && !error && courseSongs.length === 0 && (
                        <div className="music-dash-empty-state">
                            <p>No preloaded songs available for this language course yet.</p>
                            <button className="music-dash-cta-btn" onClick={() => setActiveTab("my_library")}>
                                <PlusIcon size={16} />
                                <span>Add Your Own Songs</span>
                            </button>
                        </div>
                    )}

                    {!loadingCourse && !error && courseSongs.length > 0 && (
                        <div className="music-grid">
                            {courseSongs.map((song) => (
                                <SongCard
                                    key={song.id}
                                    song={song}
                                    lang_id={lang_id}
                                    user_id={user_id}
                                    isCustom={false}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: MY LIBRARY (DYNAMIC USER SONGS & SEARCH) */}
            {activeTab === "my_library" && (
                <div className="music-tab-content">
                    {/* YouTube Search Bar */}
                    <div className="music-search-section">
                        <form className="music-search-bar" onSubmit={handleSearch}>
                            <SearchIcon size={18} />
                            <input
                                type="text"
                                className="music-search-input"
                                placeholder="Search songs on YouTube..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button type="submit" className="music-search-submit-btn" disabled={isSearching || !searchQuery.trim()}>
                                {isSearching ? "Searching..." : "Search"}
                            </button>
                        </form>

                        {/* Search Results Preview */}
                        {isSearching && (
                            <LoadingScreen inline title="Searching YouTube..." size={28} />
                        )}

                        {!isSearching && hasSearched && (
                            <div className="music-search-results-panel">
                                <div className="music-search-results-header">
                                    <h4>Search Results ({searchResults.length})</h4>
                                    <button
                                        className="music-search-results-close"
                                        onClick={() => {
                                            setHasSearched(false);
                                            setSearchResults([]);
                                        }}
                                    >
                                        Hide Results
                                    </button>
                                </div>

                                {searchResults.length === 0 ? (
                                    <p className="music-search-empty-text">No YouTube tracks found for "{searchQuery}".</p>
                                ) : (
                                    <div className="music-search-results-list">
                                        {searchResults.map((vid) => (
                                            <div key={vid.video_id} className="music-search-result-item">
                                                <img
                                                    src={vid.thumbnail}
                                                    alt={vid.title}
                                                    className="music-search-result-thumb"
                                                />
                                                <div className="music-search-result-info">
                                                    <h5 className="music-search-result-title">{vid.title}</h5>
                                                    <div className="music-search-result-meta">
                                                        <span>{vid.channel}</span>
                                                        <span>•</span>
                                                        <span>{vid.duration_string}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    className="music-search-result-add-btn"
                                                    onClick={() => setSelectedVideo(vid)}
                                                >
                                                    <PlusIcon size={14} />
                                                    <span>Add Song</span>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* User's Custom Songs Grid */}
                    <div className="music-custom-library-section">
                        <div className="music-section-subhead">
                            <h3 className="music-section-subhead__title">Your Saved Tracks</h3>
                            <span className="music-section-subhead__count">
                                {customSongs.length} track{customSongs.length === 1 ? "" : "s"}
                            </span>
                        </div>

                        {loadingCustom && <LoadingScreen inline title="Loading your library..." size={40} />}

                        {!loadingCustom && customSongs.length === 0 && (
                            <div className="music-dash-empty-state">
                                <h3>No songs added yet</h3>
                            </div>
                        )}

                        {!loadingCustom && customSongs.length > 0 && (
                            <div className="music-grid">
                                {customSongs.map((song) => (
                                    <SongCard
                                        key={song.id}
                                        song={song}
                                        lang_id={lang_id}
                                        user_id={user_id}
                                        isCustom={true}
                                        onDeleted={handleSongDeleted}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Two-Step Verification Modal */}
            {selectedVideo && (
                <SongReviewModal
                    video={selectedVideo}
                    langId={lang_id}
                    userId={user_id}
                    onClose={() => setSelectedVideo(null)}
                    onSongSaved={(song) => {
                        handleSongSaved(song);
                        setSearchQuery("");        // Clear the search bar
                        setHasSearched(false);    // Hide results
                        setSearchResults([]);
                    }}
                />
            )}
        </div>
    );
}

export default MusicDash;
